import { Token } from "@0xsquid/squid-types";
import { AccountInfo, AccountTokens } from "../../core/types/hedera";
import { convertEvmAddressToHederaAccountId } from "../internal/hederaService";

/**
 * Client for interacting with the Hedera Mirrornode API.
 *
 * @docs https://mainnet.mirrornode.hedera.com/api/v1/docs
 */
export class HederaApiClient {
  private apiUrl: string;

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  async isTokenAssociated({
    address,
    token,
  }: {
    address: string;
    token: Token;
  }): Promise<boolean> {
    const accountInfo = await this.getAccountInfo(address);

    // Unlimited auto associations
    if (accountInfo.max_automatic_token_associations === -1) {
      return true;
    }

    // If there's no unlimited auto-associations, we need to check if the token is already associated.
    const { tokens: accountTokens } = await this.getAccountTokens(address);
    const tokenId = convertEvmAddressToHederaAccountId(token.address);

    if (accountTokens.some((t) => t.token_id === tokenId)) {
      // Token is already associated
      return true;
    }

    // Finally, if not auto-associated, check if there is an available auto-association slot
    const autoAssociatedTokens = accountTokens.filter(
      (t) => t.automatic_association
    );

    const remainingAutoAssociations =
      accountInfo.max_automatic_token_associations -
      autoAssociatedTokens.length;

    return remainingAutoAssociations > 0;
  }

  async getAccountInfo(address: string): Promise<AccountInfo> {
    const data = await this.fetch<AccountInfo>(`accounts/${address}`);

    if (typeof data.max_automatic_token_associations !== "number") {
      throw new Error(
        "Invalid max_automatic_token_associations type, expected number"
      );
    }

    if (typeof data.balance.balance !== "number") {
      throw new Error("Invalid balance type, expected number");
    }

    if (!Array.isArray(data.balance.tokens)) {
      throw new Error("Invalid tokens type, expected array");
    }

    return data;
  }

  async getAccountTokens(address: string): Promise<AccountTokens> {
    const data = await this.fetch<AccountTokens>(`accounts/${address}/tokens`);

    if (!Array.isArray(data.tokens)) {
      throw new Error("Invalid tokens type, expected array");
    }

    const firstToken = data.tokens[0];

    if (typeof firstToken?.automatic_association !== "boolean") {
      throw new Error("Invalid automatic_association type, expected boolean");
    }

    if (typeof firstToken?.token_id !== "string") {
      throw new Error("Invalid token_id type, expected string");
    }

    return data;
  }

  private async fetch<T>(path: string): Promise<T> {
    const url = new URL(path, this.apiUrl);
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Hedera API error: ${response.status}`);
    }

    return response.json();
  }
}
